import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPinOff } from 'lucide-react';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { SpawnRow } from '../../components/world/SpawnRow';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { MonsterItemDrops } from '../../components/world/MonsterItemDrops';
import { EventPositionList } from '../../components/world/EventPositionList';
import { NpcCard } from '../../components/progression/NpcCard';
import { npcs } from '../../data/guide/npcs';
import { useEventPositions } from '../../hooks/useEventPositions';
import { positionsFor } from '../../utils/eventPositions';
import { EntityImage } from '../../components/world/EntityImage';
import { imageEntryForMonster } from '../../utils/entityImages';

/** Posiciones del monstruo dentro de eventos (32_event_spawn_rules), separadas de los spots permanentes. */
function MonsterEventPositions({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const ep = useEventPositions();
  const list = ep ? positionsFor(ep.positions, monsterId) : [];
  if (!list.length) return null;
  return <WorldSection id="posiciones-evento" title="Posiciones dentro de eventos" count={list.length} data-id="element-2576" data-mp="1bhpsxf98nmfc">
      <EventPositionList w={w} positions={list} max={12} data-id="element-2577" data-mp="1gganoz5q1dwe" />
    </WorldSection>;
}
import { BoxContents } from '../../components/world/BagSourceList';
import { monsterBags } from '../../utils/bagSources';
import { mapName, mapPath, formatSeconds } from '../../utils/worldFormat';
import type { Monster, WorldData } from '../../types/world';
export function MonsterDetail() {
  const {
    monsterId
  } = useParams();
  useHashScroll();
  return <WorldGate data-id="element-2578" data-mp="30x76m1y0r1o4">
      {w => {
      const m = w.monsters.get(Number(monsterId));
      if (!m) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2579" data-mp="ws18y96oscud">
              <p className="text-white" data-id="element-2580" data-mp="wtud9httukz7">No encontramos ese monstruo en los archivos.</p>
              <Link to="/guia/mapas?vista=monstruos" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2581" data-mp="1ap6jomhchzvy">Volver a Monstruos</Link>
            </div>;
      }
      return <MonsterView w={w} m={m} data-id="element-2582" data-mp="i35049wt2vsp" />;
    }}
    </WorldGate>;
}
function MonsterView({
  w,
  m
}: {
  w: WorldData;
  m: Monster;
}) {
  useEffect(() => {
    document.title = `${m.name} · Monstruos · Guía MU Vanguard`;
  }, [m.name]);
  const locations = w.idx.locationsByMonster.get(m.id) ?? [];
  // Solo NPC con identidad confirmada (monsterId); los candidatos de Pet Trainer no reciben su tarjeta.
  const guideNpc = npcs.find(n => n.monsterId === m.id);
  const permanent = locations.filter(l => l.kind === 'ordinary');
  const temporary = locations.filter(l => l.kind !== 'ordinary');
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? [];
  const spots = spawns.filter(s => s.kind === 'spot');
  const invasionSpawns = spawns.filter(s => s.kind === 'invasion');
  const eventIds = Array.from(new Set([...locations, ...spawns].map(x => x.eventId).filter((v): v is string => !!v)));
  return <div data-id="element-2583" data-mp="21zaseoadii27">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: 'Monstruos',
      to: '/guia/mapas?vista=monstruos'
    }, {
      label: m.name
    }]} eyebrow={guideNpc ? 'NPC' : 'Monstruo'} title={m.name} media={imageEntryForMonster(w, m.id)?.image ? <EntityImage entry={imageEntryForMonster(w, m.id)} alt={m.name} name={m.name} size="hero" lazy={false} kind={guideNpc ? 'persona' : 'monstruo'} data-id="element-2585" data-mp="2fsjyog30yngi" /> : undefined} data-id="element-2584" data-mp="21z102u1uvytc">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2586" data-mp="149fx0n00ehet">
          {m.level !== undefined && <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-2587" data-mp="19bf0rxznvgca">Nivel del monstruo (catálogo): {m.level}</span>}
          <CopyLinkButton label="Copiar enlace" data-id="element-2588" data-mp="1jrh3c4v4pr4w" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-2589" data-mp="1fs8e9db1ahfo">
        <WorldSection id="ubicaciones" title="Dónde aparece" data-id="element-2590" data-mp="1fp2n7siuh5hb">
          {guideNpc ?
        // NPC de las guías: misma resolución de ubicación que en misiones, recetas y buscador.
        <NpcCard npcId={guideNpc.id} data-id="element-2591" data-mp="27giisc2dq2jo" /> : locations.length === 0 ? <p className="flex items-start gap-2 rounded-2xl border border-white/10 p-4 text-gray-300" data-id="element-2592" data-mp="23hj96e5pdhlw">
              <MapPinOff className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-2593" data-mp="1yg3lsfg1yz0c" />
              Ubicación no documentada en los archivos disponibles.
            </p> : <div className="grid gap-6 md:grid-cols-2" data-id="element-2594" data-mp="n42rym4c0kdq">
              {[{
            title: 'Presencia permanente',
            list: permanent
          }, {
            title: 'Solo durante eventos o invasiones',
            list: temporary
          }].map(g => g.list.length > 0 && <div key={g.title} data-id="element-2595" data-mp="cy1kobzu40ga">
                      <h3 className="mb-2 text-sm font-semibold text-gray-400" data-id="element-2596" data-mp="cz4b6it8w0vo">{g.title}</h3>
                      <ul className="space-y-2" data-id="element-2597" data-mp="hwmiy5vj0a23">
                        {g.list.map(l => <li key={l.id} className="flex flex-wrap items-center gap-2" data-id="element-2598" data-mp="mpyv3gj0qody">
                            <Link to={mapPath(l.mapId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-2599" data-mp="qppta5avk6si">
                              {mapName(w, l.mapId)}
                            </Link>
                            {l.eventId && <EventChip w={w} eventId={l.eventId} data-id="element-2600" data-mp="15nrysl1gv2ce" />}
                          </li>)}
                      </ul>
                    </div>)}
            </div>}
        </WorldSection>

        <WorldSection id="spots" title="Spots y áreas" count={spots.length} data-id="element-2601" data-mp="37gzevwowlnzc">
          {spots.length ? <ul className="grid gap-2.5" data-id="element-2602" data-mp="2zg0bcie1pn6q">{spots.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2603" data-mp="2ug5mekdtz7ob" />)}</ul> : <p className="text-gray-400" data-id="element-2604" data-mp="39cwf6at01l4y">No hay spots permanentes configurados para este monstruo.</p>}
        </WorldSection>

        <MonsterEventPositions w={w} monsterId={m.id} data-id="element-2605" data-mp="34bp6m9j18hx8" />

        {invasionSpawns.length > 0 && <WorldSection id="invasiones" title="Apariciones en invasiones" count={invasionSpawns.length} data-id="element-2606" data-mp="3ibgs0vbvp0l9">
            <p className="mb-3 text-sm text-gray-400" data-id="element-2607" data-mp="3ddomuzm83hz4">Cada fila es un registro. Las filas de mapas alternativos no son monstruos adicionales.</p>
            <ul className="grid gap-2.5" data-id="element-2608" data-mp="3n41jhq7bb1lm">{invasionSpawns.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2609" data-mp="3n4jll3gnoa1g" />)}</ul>
          </WorldSection>}

        {eventIds.length > 0 && <WorldSection id="eventos" title="Eventos e invasiones relacionados" data-id="element-2610" data-mp="wjmywzm2hib">
            <div className="flex flex-wrap gap-2" data-id="element-2611" data-mp="uqgpiswtzau">{eventIds.map(id => <EventChip key={id} w={w} eventId={id} data-id="element-2612" data-mp="jm5ax6633l0k" />)}</div>
          </WorldSection>}

        <MonsterItemDrops w={w} monsterId={m.id} data-id="element-2613" data-mp="oivqlsdz8elg" />

        <BoxContents w={w} bags={monsterBags(w, m.id)} id="recompensas" title="Recompensas posibles al derrotarlo" intro="Lista interna de resultados posibles al derrotar a este monstruo. No es una caja que abras ni se entregan todos juntos." data-id="element-2614" data-mp="ak5t4bn8wcwk" />

        <Disclosure title="Estadísticas de catálogo" data-id="element-2615" data-mp="5l2noq3cs4o2">
          <StatList rows={[{
          label: 'Nivel (catálogo)',
          value: m.level
        }, {
          label: 'Vida (catálogo)',
          value: m.hp
        }, {
          label: 'Daño mínimo (catálogo)',
          value: m.damageMin
        }, {
          label: 'Daño máximo (catálogo)',
          value: m.damageMax
        }, {
          label: 'Defensa (catálogo)',
          value: m.defense
        }, {
          label: 'Reaparición (catálogo)',
          value: m.respawnSeconds !== undefined ? formatSeconds(m.respawnSeconds) : undefined
        }, {
          label: 'item_rate_divisor',
          value: m.itemRateDivisor,
          note: 'no es una probabilidad de drop'
        }, {
          label: 'ID del monstruo',
          value: m.id
        }]} data-id="element-2616" data-mp="5mnh2cdxlhkc" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-2617" data-mp="3pfzh571te3zx">Valores generales del catálogo. Cada spot puede tener estadísticas configuradas distintas; míralas en la ficha del spot.</p>
        </Disclosure>
      </div>
    </div>;
}